import { useEffect } from 'react';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { loadPendoScript, waitForPendo } from './pendoLoader';

// Derives the Pendo accountId from the userEntityRef namespace.
// Falls back to ownership group namespace, then tenantName, then 'unknown'.
function deriveAccountId(
  userEntityRef: string,
  ownershipEntityRefs: string[],
  tenantName: string | undefined,
): string {
  // userEntityRef format: "user:<namespace>/<login>"
  const match = userEntityRef.match(/^user:([^/]+)\//);
  const namespace = match?.[1] ?? 'default';

  if (namespace !== 'default') {
    return namespace;
  }

  // Fallback 1: extract namespace from first group ownership ref
  // ownershipEntityRef format: "group:<namespace>/<name>"
  for (const ref of ownershipEntityRefs) {
    if (ref.startsWith('group:')) {
      const groupMatch = ref.match(/^group:([^/]+)\//);
      if (groupMatch?.[1] && groupMatch[1] !== 'default') {
        return groupMatch[1];
      }
    }
  }

  // Fallback 2: tenantName from ConfigMap (e.g. "spikedev", "flow-dev")
  if (tenantName && tenantName !== 'default') {
    return tenantName;
  }

  return 'unknown';
}

// SHA-256 hash — same implementation used by GA4 in apis.ts
async function hashVisitorId(userEntityRef: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(userEntityRef);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return btoa(userEntityRef);
  }
}

export function PendoBootstrap(): null {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const enabled = configApi.getOptionalBoolean('app.analytics.pendo.enabled');
      if (!enabled) {
        return;
      }

      const apiKey = configApi.getString('app.analytics.pendo.apiKey');
      const tenantName = configApi.getOptionalString('tenantName');
      const identity = await identityApi.getBackstageIdentity();

      if (cancelled) return;

      const visitorId = await hashVisitorId(identity.userEntityRef);
      const accountId = deriveAccountId(
        identity.userEntityRef,
        identity.ownershipEntityRefs,
        tenantName,
      );

      loadPendoScript(apiKey);

      try {
        await waitForPendo(5000);
      } catch {
        // eslint-disable-next-line no-console
        console.warn(
          '[PendoBootstrap] SDK did not load within 5 s — skipping initialization',
        );
        return;
      }

      if (cancelled) return;

      window.pendo.initialize({
        visitor: { id: visitorId },
        account: { id: accountId },
      });
    }

    init();

    return () => {
      cancelled = true;
      if (window.pendo?.clearSession) {
        window.pendo.clearSession();
      }
    };
  }, [configApi, identityApi]);

  return null;
}
